import { useEffect, useState, type FormEvent } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { api } from '../lib/api';

interface Photo {
  uuid: string;
  src: string;
  width: number;
  height: number;
  title: string;
  description: string;
  photographer: string;
  price_base_cents: number;
  slug: string;
  is_active: number;
}

export function PhotoEdit() {
  const { uuid } = useParams();
  const navigate = useNavigate();
  const isNew = !uuid;
  const [photo, setPhoto] = useState<Partial<Photo>>({
    src: '',
    width: 800,
    height: 600,
    title: '',
    description: '',
    photographer: '',
    price_base_cents: 4500,
    slug: '',
    is_active: 1,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isNew) return;
    api
      .get<{ photos: Photo[] }>(`/api/admin/photos`)
      .then((d) => {
        const found = d.photos.find((p) => p.uuid === uuid);
        if (found) setPhoto(found);
      })
      .catch(console.error);
  }, [uuid, isNew]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (isNew) {
        await api.post('/api/admin/photos', photo);
      } else {
        await api.put(`/api/admin/photos/${uuid}`, photo);
      }
      navigate('/photos');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async () => {
    if (!uuid || !confirm('Deactivate this photo?')) return;
    await api.delete(`/api/admin/photos/${uuid}`);
    navigate('/photos');
  };

  return (
    <div style={{ maxWidth: 720 }}>
      <Link to="/photos" style={{ color: '#6b7280', fontSize: 13 }}>
        ← Photos
      </Link>
      <h1 style={{ fontFamily: 'Georgia, serif', fontSize: 28, fontWeight: 400, margin: '8px 0 24px' }}>
        {isNew ? 'New photo' : photo.title}
      </h1>

      <form onSubmit={onSubmit} style={{ background: '#fff', padding: 24, border: '1px solid #e5e5e5', borderRadius: 8 }}>
        <Field label="Title" value={photo.title || ''} onChange={(v) => setPhoto({ ...photo, title: v })} />
        <Field label="Slug (URL-safe)" value={photo.slug || ''} onChange={(v) => setPhoto({ ...photo, slug: v })} />
        <Field label="Image URL or R2 key" value={photo.src || ''} onChange={(v) => setPhoto({ ...photo, src: v })} />
        <Field
          label="Photographer"
          value={photo.photographer || ''}
          onChange={(v) => setPhoto({ ...photo, photographer: v })}
        />
        <FieldTextarea
          label="Description"
          value={photo.description || ''}
          onChange={(v) => setPhoto({ ...photo, description: v })}
        />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
          <NumberField
            label="Base price (cents)"
            value={photo.price_base_cents || 0}
            onChange={(v) => setPhoto({ ...photo, price_base_cents: v })}
          />
          <NumberField label="Width" value={photo.width || 0} onChange={(v) => setPhoto({ ...photo, width: v })} />
          <NumberField label="Height" value={photo.height || 0} onChange={(v) => setPhoto({ ...photo, height: v })} />
        </div>
        <label style={{ display: 'block', marginTop: 16, fontSize: 14 }}>
          <input
            type="checkbox"
            checked={photo.is_active === 1}
            onChange={(e) => setPhoto({ ...photo, is_active: e.target.checked ? 1 : 0 })}
          />{' '}
          Active (visible in storefront)
        </label>

        {error && <p style={{ color: '#b91c1c', marginTop: 16 }}>{error}</p>}

        <div style={{ display: 'flex', gap: 12, marginTop: 24 }}>
          <button type="submit" disabled={busy} style={btnPrimary}>
            {busy ? 'Saving…' : 'Save'}
          </button>
          {!isNew && (
            <button type="button" onClick={onDelete} style={btnDanger}>
              Deactivate
            </button>
          )}
        </div>
      </form>
    </div>
  );
}

function Field({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <label style={{ display: 'block', marginBottom: 12 }}>
      <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>{label}</div>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{
          width: '100%',
          padding: '8px 12px',
          border: '1px solid #e5e5e5',
          borderRadius: 6,
          fontSize: 14,
        }}
      />
    </label>
  );
}

function FieldTextarea({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <label style={{ display: 'block', marginBottom: 12 }}>
      <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>{label}</div>
      <textarea
        value={value}
        rows={3}
        onChange={(e) => onChange(e.target.value)}
        style={{
          width: '100%',
          padding: '8px 12px',
          border: '1px solid #e5e5e5',
          borderRadius: 6,
          fontSize: 14,
          fontFamily: 'inherit',
        }}
      />
    </label>
  );
}

function NumberField({ label, value, onChange }: { label: string; value: number; onChange: (v: number) => void }) {
  return (
    <label style={{ display: 'block', marginBottom: 12 }}>
      <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>{label}</div>
      <input
        type="number"
        value={value}
        onChange={(e) => onChange(parseInt(e.target.value, 10) || 0)}
        style={{
          width: '100%',
          padding: '8px 12px',
          border: '1px solid #e5e5e5',
          borderRadius: 6,
          fontSize: 14,
        }}
      />
    </label>
  );
}

const btnPrimary: React.CSSProperties = {
  padding: '10px 16px',
  background: '#1a1a1a',
  color: '#fff',
  border: 'none',
  borderRadius: 6,
  fontSize: 14,
  cursor: 'pointer',
};

const btnDanger: React.CSSProperties = {
  padding: '10px 16px',
  background: '#fff',
  color: '#b91c1c',
  border: '1px solid #fca5a5',
  borderRadius: 6,
  fontSize: 14,
  cursor: 'pointer',
};
